import { useIsMobile } from '@/hooks/use-mobile';
import { useOpenModalsCount } from '@/hooks/use-open-modals-count';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

export interface ChatwootWidgetContextValue {
  isOpen: boolean;
  isFabEnabled: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** Set by the desktop AppSidebar and by the mobile shell layouts. */
  setHasShellOnScreen: (hasShellOnScreen: boolean) => void;
}

const ChatwootWidgetContext = createContext<ChatwootWidgetContextValue | null>(
  null
);

export function ChatwootWidgetProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShellOnScreen, setHasShellOnScreen] = useState(false);
  const hasOverlayOnScreen = useOpenModalsCount().openModalsCount > 0;
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);

  // Prevent a flicker of the floating button while the sidebar hasn't mounted yet
  useEffect(() => {
    const timeout = setTimeout(() => {
      setMounted(true);
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  // On mobile inside the shell, Ajuda lives in the Mais sheet: never show the
  // FAB there (not even over a sheet). Elsewhere, the shell hides it unless
  // an overlay is open.
  const isFabEnabled =
    mounted &&
    !(isMobile && hasShellOnScreen) &&
    (!hasShellOnScreen || hasOverlayOnScreen);

  return (
    <ChatwootWidgetContext.Provider
      value={{
        isOpen,
        isFabEnabled,
        setIsOpen,
        setHasShellOnScreen,
      }}
    >
      {children}
    </ChatwootWidgetContext.Provider>
  );
}

export function useChatwootWidget() {
  const context = useContext(ChatwootWidgetContext);
  if (!context) {
    throw new Error(
      'useChatwootWidget must be used within a ChatwootWidgetProvider'
    );
  }
  return context;
}
